import { useCallback, useEffect, useMemo, useRef } from 'react';
import { message } from 'antd';

import type { UploadedImage } from '../../components/RichTextEditor';
import { DEFAULT_IMAGE_ID_ATTR } from '../../components/RichTextEditor/inlineImageSupport';
import {
	useImportProgramImageMutation,
	useUploadProgramImageMutation,
} from '../../redux/api/manufacturing';
import type { ProgramImageHandlers } from './useProgramImages';

/**
 * Stands in for a `ProgramImage` id while the bytes are still in the browser.
 * Stored ids are UUIDs, so a prefixed counter can never collide with one.
 */
const QUEUED_ID_PREFIX = 'queued-';

/** An image the editor accepted before there was a program to attach it to. */
type QueuedImage =
	| { kind: 'file'; file: File }
	| { kind: 'url'; url: string };

const hasImages = (html: string) => html.includes('<img');

const parseBody = (html: string) =>
	new DOMParser().parseFromString(html, 'text/html');

const queuedImageNodes = (doc: Document) =>
	Array.from(
		doc.querySelectorAll<HTMLImageElement>(`img[${DEFAULT_IMAGE_ID_ATTR}]`)
	).filter((image) =>
		(image.getAttribute(DEFAULT_IMAGE_ID_ATTR) ?? '').startsWith(
			QUEUED_ID_PREFIX
		)
	);

/** Ids of the images in `html` that are still waiting to be stored. */
export function queuedImageIds(html: string | null | undefined): string[] {
	if (!html || !hasImages(html)) {
		return [];
	}
	return queuedImageNodes(parseBody(html)).map(
		(image) => image.getAttribute(DEFAULT_IMAGE_ID_ATTR) as string
	);
}

/**
 * `html` with the not-yet-stored images taken out.
 *
 * A queued image points at a `blob:` URL that means nothing outside this tab,
 * so it is what the first save of a new program leaves behind: the prose is
 * recorded even if storing the pictures afterwards fails.
 */
export function withoutQueuedImages(html: string): string {
	if (!hasImages(html)) {
		return html;
	}
	const doc = parseBody(html);
	queuedImageNodes(doc).forEach((image) => image.remove());
	return doc.body.innerHTML;
}

/**
 * `html` with each queued image repointed at the row it was stored as.
 *
 * An image missing from `stored` failed to upload and is dropped rather than
 * left behind: a visibly absent picture beats one that renders here and 404s
 * for the next reader.
 */
export function withStoredQueuedImages(
	html: string,
	stored: Map<string, UploadedImage>
): string {
	if (!hasImages(html)) {
		return html;
	}
	const doc = parseBody(html);
	queuedImageNodes(doc).forEach((image) => {
		const uploaded = stored.get(
			image.getAttribute(DEFAULT_IMAGE_ID_ATTR) as string
		);
		if (!uploaded) {
			image.remove();
			return;
		}
		image.setAttribute('src', uploaded.url);
		image.setAttribute(DEFAULT_IMAGE_ID_ATTR, uploaded.id);
	});
	return doc.body.innerHTML;
}

export interface QueuedProgramImages {
	/** Image handlers to spread onto a `RichTextEditor`. */
	imageProps: ProgramImageHandlers;
	/**
	 * Stores everything `html` queued against `programId` and returns the HTML
	 * pointing at the stored rows. Safe to call twice — an image stored by an
	 * earlier attempt is not uploaded again.
	 */
	attachQueuedImages: (programId: string, html: string) => Promise<string>;
	/** Forgets the queue. Call when the form is dismissed. */
	reset: () => void;
}

/**
 * Program image handling for a form that may not have a program yet.
 *
 * With a `programId` this behaves like {@link useProgramImages}: every paste
 * goes straight to the server. Without one the image is held in the browser and
 * shown from a local URL, because the upload endpoint is program-scoped and a
 * program the user has not saved yet has nowhere to put it. Calling
 * `attachQueuedImages` once the program exists stores the backlog and hands
 * back the HTML to save.
 *
 * Holding the bytes rather than creating a placeholder program keeps a
 * cancelled form from leaving a phantom row in the scoring grid, and keeps
 * "log another program" logging a second one instead of overwriting the first.
 */
export function useQueuedProgramImages(
	programId: string | null | undefined
): QueuedProgramImages {
	const [uploadImage] = useUploadProgramImageMutation();
	const [importImage] = useImportProgramImageMutation();

	// The editor's paste handlers are built once, so everything they read has
	// to be a ref rather than a value captured at mount.
	const programIdRef = useRef(programId);
	programIdRef.current = programId;
	const queue = useRef(new Map<string, QueuedImage>());
	const stored = useRef(new Map<string, UploadedImage>());
	const previews = useRef<string[]>([]);
	const lastId = useRef(0);

	const reset = useCallback(() => {
		queue.current.clear();
		stored.current.clear();
		previews.current.forEach((url) => URL.revokeObjectURL(url));
		previews.current = [];
	}, []);

	// Preview URLs outlive the editor unless they are revoked, so the queue is
	// dropped when the form is torn down as well as when it is dismissed.
	useEffect(() => () => reset(), [reset]);

	const enqueue = useCallback(
		(image: QueuedImage, previewUrl: string): UploadedImage => {
			lastId.current += 1;
			const id = `${QUEUED_ID_PREFIX}${lastId.current}`;
			queue.current.set(id, image);
			return { id, url: previewUrl };
		},
		[]
	);

	const imageProps = useMemo<ProgramImageHandlers>(
		() => ({
			onUploadImage: async (file) => {
				const target = programIdRef.current;
				if (target) {
					return uploadImage({ programId: target, file }).unwrap();
				}
				const preview = URL.createObjectURL(file);
				previews.current.push(preview);
				return enqueue({ kind: 'file', file }, preview);
			},
			onImportImageUrl: async (url) => {
				const target = programIdRef.current;
				if (target) {
					return importImage({ programId: target, url }).unwrap();
				}
				// The source URL is good enough to preview from; the server
				// fetches its own copy when the program exists.
				return enqueue({ kind: 'url', url }, url);
			},
		}),
		[uploadImage, importImage, enqueue]
	);

	const attachQueuedImages = useCallback(
		async (target: string, html: string) => {
			const ids = queuedImageIds(html);
			if (ids.length === 0) {
				return html;
			}

			await Promise.all(
				ids.map(async (id) => {
					const queued = queue.current.get(id);
					// Already stored by a previous attempt that failed later on.
					if (!queued || stored.current.has(id)) {
						return;
					}
					try {
						const uploaded =
							queued.kind === 'file'
								? await uploadImage({
										programId: target,
										file: queued.file,
									}).unwrap()
								: await importImage({
										programId: target,
										url: queued.url,
									}).unwrap();
						stored.current.set(id, uploaded);
					} catch {
						// Reported below, once, however many failed.
					}
				})
			);

			const lost = ids.filter((id) => !stored.current.has(id)).length;
			if (lost > 0) {
				message.error(
					lost === 1
						? 'One image could not be saved to the notes.'
						: `${lost} images could not be saved to the notes.`
				);
			}

			return withStoredQueuedImages(html, stored.current);
		},
		[uploadImage, importImage]
	);

	return { imageProps, attachQueuedImages, reset };
}
